import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { cn } from '@/lib/utils';
import type { Notification } from '../types';
import {
  useNotificationMutations,
  useNotifications,
  useUnreadNotificationCount,
} from '../hooks/useNotifications';
import { openNotificationLink } from '../utils/openNotificationLink';

const PAGE_SIZE = 20;

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

interface NotificationListItemProps {
  notification: Notification;
  onOpen: (notification: Notification) => void;
}

const NotificationListItem: React.FC<NotificationListItemProps> = ({ notification, onOpen }) => (
  <button
    type="button"
    className={cn(
      'w-full border-b border-border px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-accent/70',
      !notification.is_read && 'bg-accent/40',
    )}
    onClick={() => onOpen(notification)}
  >
    <div className="flex gap-3">
      <span
        className={cn(
          'mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full',
          notification.is_read ? 'bg-transparent' : 'bg-primary',
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-medium text-foreground">{notification.title}</h3>
          <span className="text-sm text-muted-foreground">{formatDate(notification.created_at)}</span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{notification.message}</p>
        {notification.link_label && (
          <p className="mt-2 text-sm font-medium text-primary">{notification.link_label}</p>
        )}
      </div>
    </div>
  </button>
);

export const NotificationsList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching } = useNotifications(page, PAGE_SIZE);
  const { data: unreadCount } = useUnreadNotificationCount();
  const { markRead, markAllRead } = useNotificationMutations();

  const notifications = data?.results ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const unread = unreadCount?.count ?? 0;

  const openNotification = async (notification: Notification) => {
    if (!notification.is_read) {
      await markRead.mutateAsync(notification.id);
    }
    openNotificationLink(notification.link_url, navigate);
  };

  const handleMarkAllRead = async () => {
    await markAllRead.mutateAsync();
  };

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review updates and jump back to related work.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleMarkAllRead}
          disabled={unread === 0 || markAllRead.isPending}
        >
          {markAllRead.isPending ? 'Marking read...' : 'Mark all read'}
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 px-5 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading notifications...
        </div>
      )}

      {isError && (
        <div className="px-5 py-12 text-center text-sm text-muted-foreground">
          Notifications could not be loaded.
        </div>
      )}

      {!isLoading && !isError && notifications.length === 0 && (
        <div className="px-5 py-12 text-center text-sm text-muted-foreground">
          No notifications yet.
        </div>
      )}

      {!isLoading &&
        !isError &&
        notifications.map((notification) => (
          <NotificationListItem
            key={notification.id}
            notification={notification}
            onOpen={openNotification}
          />
        ))}

      {!isLoading && !isError && data && data.count > 0 && (
        <div className="flex flex-col gap-3 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, data.count)} of{' '}
            {data.count}
          </p>
          <div className="flex items-center gap-2">
            {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || page === 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || page >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
